// localStorage cache for the patient's medicine list + an auto-scan
// completion stamp.
//
// Why localStorage: the list payload is small (well under 100 KB even for
// long histories) and per-device, and persisting it across sessions means
// opening `/patient/meds` or the doctor's meds modal feels immediate
// after the very first visit — no skeleton flash, no "Reading visits…"
// spinner unless the patient is genuinely brand new.
//
// We use a single "current" key rather than namespacing per user because
// the patient portal is single-signed-in per device: there's no scenario
// in the product where two different patient accounts share a browser
// without a sign-out in between, and a sign-out already resets all client
// state. If that ever changes, switch `storageKey()` to take a uid and
// add a `getCurrentUserKey()`-style resolver.
//
// Cache contents: `{ savedAt, meds: { current: [...], history: [...] } }`
// where the inner arrays are already in the mapped shape the pages
// consume (the result of `mapMed` / `mapCurrent`), so we can `setState`
// directly from cache without re-running the mapper on the slow path.
//
// The auto-scan stamp records the last time we successfully ran the
// "Add meds from visits" flow for this patient, so repeat page opens
// don't keep firing the expensive LLM round-trip.

const TTL_MS = 7 * 24 * 60 * 60 * 1000;

const CACHE_KEY = "patientMeds.cache:v1:current";
const STAMP_KEY_DAY = "patientMeds.autoScan.day:v1:current";
const STAMP_KEY_EVER = "patientMeds.autoScan.ever:v1:current";

export function loadMedsCacheSync() {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.savedAt !== "number") return null;
    if (Date.now() - parsed.savedAt > TTL_MS) return null;
    if (!parsed.meds || typeof parsed.meds !== "object") return null;
    return parsed.meds;
  } catch (_) {
    return null;
  }
}

export function readMedsCache() {
  return loadMedsCacheSync();
}

export function writeMedsCache(medsShape) {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ savedAt: Date.now(), meds: medsShape })
    );
  } catch (_) {
    // Quota / private mode — silently skip. The next refresh will retry.
  }
}

export function clearMedsCache() {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(CACHE_KEY);
  } catch (_) {}
}

// "Has a scan already completed today?" — same semantics the doctor
// modal used with sessionStorage, but persisted so a new tab or a hard
// refresh inside the same day doesn't re-fire the scan.
export function readAutoScanStampToday() {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(STAMP_KEY_DAY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

export function writeAutoScanStampToday(payload) {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      STAMP_KEY_DAY,
      JSON.stringify({ ...payload, writtenAt: new Date().toISOString() })
    );
  } catch (_) {}
}

// "Has a scan ever completed for this device?" — used by the patient
// page so the "scan once on first opening" rule survives reloads.
export function readAutoScanStampEver() {
  try {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(STAMP_KEY_EVER) === "1";
  } catch (_) {
    return false;
  }
}

export function writeAutoScanStampEver() {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STAMP_KEY_EVER, "1");
  } catch (_) {}
}

export function clearAutoScanStampEver() {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(STAMP_KEY_EVER);
  } catch (_) {}
}

// Local-day key in the user's wall-clock timezone (not UTC).
export function localDateKey(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}